import { useEffect, useRef } from 'react';
import { useRouter } from 'next/router';
import { useQuery } from '@apollo/client';
import { usePlatformSource } from './usePlatformSource';
import { useQueryParam } from './useQueryParam';
import { usePageType } from './usePageType';
import { PageType } from './usePageType/usePageType.types';
import { QUERY } from '@/constants/common';
import { GetAdDetailsResponse } from '@/api/types/ads.types';
import { GET_AD_DETAILS } from '@/api/queries/ads.queries';
import { getPathWithoutHash, hideParamFromUrl } from '@/utils/helpers';
import { ErrorCodesEnum } from '@/api/types/common.types';

interface UseAdDetailsOptions {
  skipQuery?: boolean;
  cacheOnly?: boolean;
}

const usePreviousRoute = () => {
  const { asPath } = useRouter();
  const ref = useRef<string | null>(null);
  const previousRef = useRef<string | null>(null);

  useEffect(() => {
    previousRef.current = ref.current;
    ref.current = asPath;
  }, [asPath]);

  return previousRef.current;
};

export function useAdDetails({
  skipQuery = false,
  cacheOnly = false,
}: UseAdDetailsOptions = {}) {
  const {
    query: { slug },
    asPath,
  } = useRouter();
  const source = usePlatformSource();
  const [transitionSourceBlock] = useQueryParam(QUERY.transitionSourceBlock);
  const pageType = usePageType();
  const previousAsPath = usePreviousRoute();

  const cleanPath = getPathWithoutHash(asPath);
  const cleanPreviousPath = getPathWithoutHash(previousAsPath);
  const legacyId = Array.isArray(slug) ? slug[2] : undefined;
  const isAdPage = pageType === PageType.Ad && !!legacyId;

  const { data, loading, refetch, previousData, error } =
    useQuery<GetAdDetailsResponse>(GET_AD_DETAILS, {
      variables: {
        legacyId,
        source,
        ...(transitionSourceBlock && { transitionSourceBlock }),
      },
      errorPolicy: 'all',
      fetchPolicy: cacheOnly ? 'cache-only' : 'cache-first',
      nextFetchPolicy: cacheOnly ? 'cache-only' : 'network-only',
      skip: !isAdPage || skipQuery,
    });

  const hasAdNotFoundError = !!error?.graphQLErrors?.some(
    ({ extensions }) => extensions?.code === ErrorCodesEnum.NotFoundError
  );

  useEffect(() => {
    if (cacheOnly || previousAsPath === null) {
      return;
    }

    if (cleanPreviousPath !== cleanPath && isAdPage) {
      refetch();
    }
  }, [cacheOnly, cleanPath, cleanPreviousPath, isAdPage, refetch]);

  useEffect(() => {
    if (isAdPage) {
      hideParamFromUrl(QUERY.transitionSourceBlock);
    }
  }, [isAdPage]);

  return {
    adDetails: data?.adDetails,
    loading,
    refetch,
    previousData,
    hasAdNotFoundError,
  };
}
